import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Briefcase,
  Users,
  UserCheck,
  Building2,
  Clock,
  DollarSign,
  TrendingUp,
  GraduationCap,
  UserX,
  ShieldCheck,
  BarChart3,
  Layers,
  Settings,
  FolderCheck,
  FileCheck
} from 'lucide-react';

const titleIconMap = {
  'Recruitment': Briefcase,
  'Talent Acquisition': Briefcase,
  'Onboarding': UserCheck,
  'Employee Directory': Users,
  'Time & Attendance': Clock,
  'Attendance & Leave': Clock,
  'Payroll': DollarSign,
  'Performance': TrendingUp,
  'Learning & Development': GraduationCap,
  'Offboarding': UserX,
  'Compliance & Policy': ShieldCheck,
  'HR Analytics': BarChart3,
  'Lead Management': Layers,
  'HR Overview': FolderCheck
};

const titleSubtitleMap = {
  'Recruitment': 'Manage open positions, candidate pipelines, interviews, and job offers',
  'Talent Acquisition': 'Track job postings and hiring workflows across departments',
  'Onboarding': 'Streamline candidate verification, orientation, and staff integration',
  'Employee Directory': 'Centralized employee profiles, departmental records, and contacts',
  'Time & Attendance': 'Monitor attendance logs, shifts, leave applications, and time tracking',
  'Payroll': 'Manage salary structures, payslips, tax deductions, and disbursements',
  'Performance': 'Track employee appraisals, goals, reviews, and KPI scores',
  'Learning & Development': 'Manage training courses, certifications, and skill upgrades',
  'Offboarding': 'Handle resignations, exit clearances, and final settlements',
  'Compliance & Policy': 'Maintain corporate guidelines, audit logs, and policy documents',
  'HR Analytics': 'Gain actionable insights into workforce metrics and hiring trends'
};

/**
 * ModuleLayout
 * A standardized layout for all HRMS/CRM modules.
 * Includes a unified enterprise header with icon box, subtitle, tabs, and content area.
 */
const ModuleLayout = ({
    title,
    subtitle,
    icon: CustomIcon,
    action,
    tabs = [],
    activeTab,
    onTabChange,
    children,
    showBack = false,
    onBack
}) => {
    const navigate = useNavigate();
    const IconComponent = CustomIcon || titleIconMap[title] || Layers;
    const resolvedSubtitle = subtitle || titleSubtitleMap[title] || '';

    const handleBack = () => {
        if (onBack) {
            onBack();
        } else {
            navigate(-1);
        }
    };

    return (
        <div className="flex flex-col h-full bg-transparent gap-5">
            {/* Standardized Enterprise Module Header */}
            <div className="bg-white border border-gray-100 rounded-2xl px-6 pt-5 pb-0 flex-shrink-0 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3.5">
                        {showBack && (
                            <button
                                onClick={handleBack}
                                className="p-2 -ml-2 text-[#64748B] hover:text-[#0F2942] rounded-full hover:bg-slate-100 transition-colors"
                                aria-label="Go back"
                            >
                                <ArrowLeft className="w-5 h-5 stroke-[2]" />
                            </button>
                        )}
                        <div className="w-10 h-10 rounded-full bg-[#0F2942] text-white flex items-center justify-center shadow-sm flex-shrink-0">
                            <IconComponent className="w-5 h-5 stroke-[2]" />
                        </div>
                        <div>
                            <h1 className="text-[22px] font-bold text-[#0F2942] leading-tight font-sans">{title}</h1>
                            {resolvedSubtitle && (
                                <p className="text-[12px] text-[#64748B] font-medium font-sans mt-0.5">{resolvedSubtitle}</p>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center space-x-3">
                        {action}
                    </div>
                </div>

                {/* Tabs (SaaS Segmented Pill Container) */}
                {tabs.length > 0 && (
                    <div className="bg-slate-100/90 p-1.5 rounded-2xl border border-slate-200/80 flex items-center space-x-1.5 w-full max-w-full overflow-x-auto custom-scrollbar mb-2">
                        {tabs.map((tab) => {
                            const IconComp = tab.icon;
                            const isActive = activeTab === tab.id;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => onTabChange(tab.id)}
                                    className={`
                                      py-2 px-3.5 sm:px-4 rounded-xl font-bold text-xs sm:text-sm flex items-center space-x-2 whitespace-nowrap transition-all duration-200 font-sans cursor-pointer flex-shrink-0
                                      ${isActive
                                            ? 'bg-white text-blue-700 shadow-sm border border-slate-200/70 scale-[1.01]'
                                            : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'}
                                    `}
                                >
                                    {IconComp && <IconComp className={`w-4 h-4 ${isActive ? 'text-blue-600' : 'text-slate-400'}`} />}
                                    <span>{tab.label}</span>
                                    {tab.count !== undefined && (
                                        <span className={`ml-1.5 py-0.5 px-2 rounded-full text-[10px] font-extrabold ${isActive ? 'bg-blue-50 text-blue-700' : 'bg-slate-200 text-slate-600'
                                            }`}>
                                            {tab.count}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto p-5 scroll-smooth custom-scrollbar">
                {children}
            </div>
        </div>
    );
};

export default ModuleLayout;
